/**
 * UX-03-A Planning product presentation helpers (frontend-only).
 * Does not change API enums or Day contracts.
 */

import { LIFECYCLE_PRODUCT_LABELS } from '../journey-workspace/journeyLifecycleModel';

export const ACTIVITY_TYPE_PRODUCT_LABELS = Object.freeze({
  CUSTOM: '自訂',
  PLACE: '地點',
  FOOD: '美食',
  EVENT: '活動',
  FREE_TIME: '自由時間',
  MEETING_POINT: '集合點',
  SHOPPING: '購物',
  TRANSPORT: '交通',
  HOTEL: '住宿',
});

export const ACTIVITY_STATUS_PRODUCT_LABELS = Object.freeze({
  PLANNED: null, // default — do not show in primary chrome
  CONFIRMED: '已確認',
  IN_PROGRESS: '進行中',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
});

/** Unified journey lifecycle product labels (closes Draft vs 規劃中 split). */
export function journeyLifecycleProductLabel(status) {
  const key = String(status || '');
  return LIFECYCLE_PRODUCT_LABELS[key] || (key ? key : '未知狀態');
}

export function journeyHasDateRange(journey) {
  return Boolean(journey?.startDate && journey?.endDate);
}

export function activityTypeProductLabel(type) {
  const key = String(type || 'CUSTOM');
  return ACTIVITY_TYPE_PRODUCT_LABELS[key] || '行程安排';
}

export function activityStatusProductLabel(status) {
  const key = String(status || '');
  if (!key || key === 'PLANNED') return null;
  if (Object.prototype.hasOwnProperty.call(ACTIVITY_STATUS_PRODUCT_LABELS, key)) {
    return ACTIVITY_STATUS_PRODUCT_LABELS[key];
  }
  return '狀態更新';
}

export const ACTIVITY_TYPE_OPTIONS = Object.freeze(
  Object.entries(ACTIVITY_TYPE_PRODUCT_LABELS)
    .filter(([value]) => ['CUSTOM', 'PLACE', 'FOOD', 'EVENT', 'FREE_TIME', 'MEETING_POINT', 'SHOPPING'].includes(value))
    .map(([value, label]) => ({ value, label })),
);

const MONTH_DAY = new Intl.DateTimeFormat('zh-TW', { month: 'long', day: 'numeric' });

/**
 * Primary day heading: date first, Day N secondary.
 * Example: `12月1日・第 1 天`
 */
export function formatPlanningDayHeading(day) {
  const order = Number(day?.displayOrder) || 0;
  const dayPart = order > 0 ? `第 ${order} 天` : '行程日';
  if (!day?.date) {
    return day?.title ? `${dayPart}・${day.title}` : dayPart;
  }
  const parsed = parseLocalDate(day.date);
  const datePart = parsed ? MONTH_DAY.format(parsed) : day.date;
  if (day?.title) return `${datePart}・${dayPart}・${day.title}`;
  return `${datePart}・${dayPart}`;
}

export function formatPlanningDayNavLabel(day) {
  if (day?.date) {
    const parsed = parseLocalDate(day.date);
    if (parsed) {
      return `${parsed.getMonth() + 1}/${parsed.getDate()}`;
    }
    return day.date;
  }
  const order = Number(day?.displayOrder) || 0;
  return order > 0 ? `第 ${order} 天` : '行程日';
}

function parseLocalDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function planningConflictUserMessage(cause) {
  const key = String(cause?.raw?.key || '');
  if (key.includes('day.has.activities')) {
    return '這一天還有安排，請先移除安排後再刪除。';
  }
  if (key.includes('day.duplicate.date')) {
    return '這個日期已經有對應的行程日。';
  }
  if (key.includes('day.out.of.range')) {
    return '這個日期不在旅程範圍內。';
  }
  if (key.includes('lifecycle')) {
    return '目前旅程狀態無法再編輯規劃。';
  }
  return '行程已在其他地方更新，請重新整理後再試。';
}
